import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { apiSlice } from "./api/apiSlice";
import { useMentorship } from "./hooks/useMentorship";
import Layout from "./Layout/Layout";
import Home from "./pages/Home/Home";
import NotFound from "./pages/NotFound/NotFound";
import FAQPage from "./pages/FAQ/FAQ";
import AboutPage from "./pages/About/About";
import ContactUs from "./pages/ContactUs/ContactUs";
import Publications from "./pages/Publications/Publications";
import Awards from "./pages/Awards/Awards";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import Membership from "./pages/Membership/Membership";
import FindExpert from "./pages/FindExpert/FindExpert";
import FindInstitution from "./pages/FindInstitution/FindInstitution";
// import ProtectedRoute from "./routes/ProtectedRoute";
// import PrivateRoute from "./routes/PrivateRoute";
import AuthModal from "./components/AuthModal/AuthModal";
import MemberProfile from "./pages/MemberProfile/MemberProfile";
import InstitutionProfile from "./pages/InstitutionProfile/InstitutionProfile";
import KnowledgeSeriesEvent from "./pages/KnowledgeSeriesEvent/KnowledgeSeriesEvent";
import ResourcesEngagement from "./pages/ResourcesEngagement/ResourcesEngagement";
import Governance from "./pages/Governance/Governance";
import PoliciesAndTerms from "./pages/PoliciesAndTerms/PoliciesAndTerms";
import { ToastContainer } from "react-toastify";
import DashboardLayout from "./Layout/DashboardLayout";
import RoleRoute from "./routes/RoleRoute";
/** Insitutions pages */
import InstitutionDashboard from "./pages/Dashboard/Institution";
import InsituteProfile from "./pages/Dashboard/Institution/Profile/Profile";
import EventManagement from "./pages/Dashboard/Institution/EventManagement";
import LinkedMembers from "./pages/Dashboard/Institution/LinkedMembers";
import ReportsCertificates from "./pages/Dashboard/Institution/ReportsCertificates";
import Communications from "./pages/Dashboard/Institution/Communications";
import ReportsCompliance from "./pages/Dashboard/Institution/ReportsCompliance";
/** Insitutions pages ends */
/** author pages */
import AuthorDashboard from "./pages/Dashboard/AuthorMentor";
import AuthorProfile from "./pages/Dashboard/AuthorMentor/Profile/Profile";
import AuthorMembershipPage from "./pages/Dashboard/AuthorMentor/Membership";
import AuthorEventDownload from "./pages/Dashboard/AuthorMentor/EventDownloads";
import AuthorCertificates from "./pages/Dashboard/AuthorMentor/Certificates";
import AuthorMessages from "./pages/Dashboard/AuthorMentor/Messages";
import AuthorSupport from "./pages/Dashboard/AuthorMentor/Support";
import MyPublications from "./pages/Dashboard/AuthorMentor/MyPublications";
import JournalFinder from "./pages/Dashboard/AuthorMentor/JournalFinder";
/** author pages ends */
import "react-toastify/dist/ReactToastify.css";

function AppPrefetch() {
  const { mentorshipOffered } = useMentorship();
  const prefetchSubjectExpertise = apiSlice.usePrefetch("getSubjectExpertise");
  const prefetchTopMenus = apiSlice.usePrefetch("getTopMenus");
  const prefetchQuickLinks = apiSlice.usePrefetch("getQuickLinks");
  const prefetchAboutMenus = apiSlice.usePrefetch("getAboutMenus");
  const prefetchSiteInformation = apiSlice.usePrefetch("getSiteInformation");

  React.useEffect(() => {
    prefetchSubjectExpertise(undefined, { force: false });
    prefetchTopMenus(undefined, { force: false });
    prefetchQuickLinks(undefined, { force: false });
    prefetchAboutMenus(undefined, { force: false });
    prefetchSiteInformation(undefined, { force: false });
  }, [
    prefetchAboutMenus,
    prefetchQuickLinks,
    prefetchSiteInformation,
    prefetchSubjectExpertise,
    prefetchTopMenus,
  ]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppPrefetch />
        <ScrollToTop />
        {/* ✅ Toast container (must exist once globally) */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <Routes>
          <Route element={<Layout />}>
            {/* Public Routes Accessable to all */}
            <Route path="/" element={<Home />} />
            <Route path="/policy-terms" element={<PoliciesAndTerms />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/publications" element={<Publications />} />
            <Route
              path="/resources-engagement"
              element={<ResourcesEngagement />}
            />
            <Route path="/governance" element={<Governance />} />
            <Route path="/awards" element={<Awards />} />
            <Route path="/membership" element={<Membership />} />
            {/* Listing Pages */}
            <Route path="/mentors" element={<FindExpert />} />
            <Route path="/authors" element={<FindExpert />} />
            <Route path="/institutions" element={<FindInstitution />} />
            {/* Individual Profile Pages */}
            <Route path="/mentors/:id" element={<MemberProfile />} />
            <Route path="/authors/:id" element={<MemberProfile />} />
            <Route path="/institutions/:id" element={<InstitutionProfile />} />
            <Route
              path="/knowledge-series-events"
              element={<KnowledgeSeriesEvent />}
            />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route
            path="/dashboard/institution"
            element={
              <RoleRoute allowed={["institution"]}>
                <DashboardLayout />
              </RoleRoute>
            }
          >
            <Route index element={<InstitutionDashboard />} />
            <Route path="profile" element={<InsituteProfile />} />
            <Route path="event-management" element={<EventManagement />} />
            <Route path="linked-members" element={<LinkedMembers />} />
            <Route
              path="reports-certificates"
              element={<ReportsCertificates />}
            />
            <Route path="communications" element={<Communications />} />
            <Route
              path="resources-compliance"
              element={<ReportsCompliance />}
            />
          </Route>
          <Route
            path="/dashboard/:role"
            element={
              <RoleRoute allowed={["mentor", "author"]}>
                <DashboardLayout />
              </RoleRoute>
            }
          >
            <Route index element={<AuthorDashboard />} />
            <Route path="profile" element={<AuthorProfile />} />
            <Route path="membership" element={<AuthorMembershipPage />} />
            <Route path="events" element={<AuthorEventDownload />} />
            <Route path="certificates" element={<AuthorCertificates />} />
            <Route path="messages" element={<AuthorMessages />} />
            <Route path="support" element={<AuthorSupport />} />
            <Route path="my-publication" element={<MyPublications />} />
            <Route path="journal-finder" element={<JournalFinder />} />
          </Route>
        </Routes>
        {/* Always available global modal */}
        <AuthModal />
      </AuthProvider>
    </BrowserRouter>
  );
}
